import React from 'react';
import { generateUUID } from '../../../../helper/uiFunctions';
import { getValueFromKey } from '../../docAutomation/formFunctions';
import {
    deletePlanWeekSetting,
    fetchPlanMonthSummary,
    fetchPlanWeekSummary,
    requestPlanWeekUnlockOtp,
    savePlanWeekChanges,
    savePlanWeekSetting,
    verifyPlanWeekUnlockOtp
} from '../calendarFunctions';

const useCalendarControl = (controlViewData) => {
    const [settings, setSettings] = React.useState({});
    const [settingKeys, setSettingKeys] = React.useState({});
    const [documentTypeId, setDocumentTypeId] = React.useState('');
    const [boardType, setBoardType] = React.useState('');
    const [boardId, setBoardId] = React.useState(0);
    const [viewId, setViewId] = React.useState(0);
    const [otpReference] = React.useState(() => generateUUID());
    const [planLockMode, setPlanLockMode] = React.useState('none');
    const [isPlanLocked, setIsPlanLocked] = React.useState(false);
    const [isCalendarReadOnly, setIsCalendarReadOnly] = React.useState(false);

    React.useEffect(() => {
        const nextPlanLockMode = getValueFromKey(controlViewData, 'planLockMode', 'none') || 'none';
        const nextDocumentTypeId = getValueFromKey(controlViewData, 'documentTypeId', '');
        const nextBoardType = getValueFromKey(controlViewData, 'boardType', '');
        const nextBoardId = Number(getValueFromKey(controlViewData, 'boardId', 0) || 0);
        const nextViewId = Number(getValueFromKey(controlViewData, 'viewId', 0) || 0);
        const nextSettings = {
            displayMode: getValueFromKey(controlViewData, 'displayMode', ''),
            snapMode: getValueFromKey(controlViewData, 'snapMode', '')
        };
        const nextSettingKeys = nextDocumentTypeId ? {
            displayMode: `${nextDocumentTypeId}_plan_displayMode`,
            snapMode: `${nextDocumentTypeId}_plan_snapMode`
        } : {};

        // console.log('calendar control server data', {
        //     controlViewData,
        //     nextDocumentTypeId,
        //     nextPlanLockMode,
        //     nextSettings,
        //     nextSettingKeys
        // });

        setSettings(nextSettings);
        setSettingKeys(nextSettingKeys);
        setDocumentTypeId(nextDocumentTypeId);
        setBoardType(nextBoardType);
        setBoardId(nextBoardId);
        setViewId(nextViewId);
        setPlanLockMode(nextPlanLockMode);
        setIsPlanLocked(nextPlanLockMode === 'locked');
        setIsCalendarReadOnly(nextPlanLockMode === 'locked');
    }, [controlViewData]);

    const setSetting = React.useCallback(async (key, value, explicitSettingKey = '') => {
        setSettings((previousSettings) => ({
            ...previousSettings,
            [key]: value
        }));

        const resolvedSettingKey = explicitSettingKey || settingKeys[key];
        if (!documentTypeId || !resolvedSettingKey) return null;

        return savePlanWeekSetting(documentTypeId, resolvedSettingKey, value);
    }, [documentTypeId, settingKeys]);

    const buildPlanSettingKey = React.useCallback((settingKey) => {
        if (!documentTypeId || !settingKey) return '';

        return `${documentTypeId}_plan_${settingKey}`;
    }, [documentTypeId]);

    const saveWeekChanges = React.useCallback(async (changeSort, changePlanDate) => {
        if (!documentTypeId) return null;

        return savePlanWeekChanges(documentTypeId, boardType, boardId, viewId, changeSort, changePlanDate);
    }, [boardId, boardType, documentTypeId, viewId]);

    const getSetting = React.useCallback((key, defaultValue = '') => {
        const currentValue = settings[key];
        return currentValue === undefined || currentValue === null || currentValue === ''
            ? defaultValue
            : currentValue;
    }, [settings]);

    const getSettingKey = React.useCallback((key, defaultValue = '') => {
        const currentValue = settingKeys[key];
        return currentValue === undefined || currentValue === null || currentValue === ''
            ? defaultValue
            : currentValue;
    }, [settingKeys]);

    const lockPlan = React.useCallback(async () => {
        const planUnlockUntilKey = buildPlanSettingKey('plan_unlock_until');
        let response = null;

        if (documentTypeId && planUnlockUntilKey) {
            response = await deletePlanWeekSetting(documentTypeId, planUnlockUntilKey);

            if (response?.data?.isError === 1) {
                return response;
            }
        }

        setPlanLockMode('locked');
        setIsPlanLocked(true);
        setIsCalendarReadOnly(true);
        return response;
    }, [buildPlanSettingKey, documentTypeId]);

    const unlockPlan = React.useCallback(() => {
        setPlanLockMode('unlocked');
        setIsPlanLocked(false);
        setIsCalendarReadOnly(false);
    }, []);

    const requestUnlockOtp = React.useCallback(async () => {
        if (!documentTypeId) return null;

        return requestPlanWeekUnlockOtp(documentTypeId, otpReference);
    }, [documentTypeId, otpReference]);

    const verifyUnlockOtp = React.useCallback(async (otp) => {
        if (!documentTypeId) return null;

        return verifyPlanWeekUnlockOtp(documentTypeId, otp, otpReference);
    }, [documentTypeId, otpReference]);

    const getWeekSummary = React.useCallback(async (searchResultsCriteria, weekEndDates) => {
        if (!documentTypeId) return null;

        return fetchPlanWeekSummary(documentTypeId, searchResultsCriteria, weekEndDates);
    }, [documentTypeId]);

    const getMonthSummary = React.useCallback(async (searchResultsCriteria, monthKeys) => {
        if (!documentTypeId) return null;

        return fetchPlanMonthSummary(documentTypeId, searchResultsCriteria, monthKeys);
    }, [documentTypeId]);

    return {
        documentTypeId,
        boardType,
        boardId,
        viewId,
        otpReference,
        settings,
        settingKeys,
        planLockMode,
        isPlanLocked,
        isCalendarReadOnly,
        setSetting,
        saveWeekChanges,
        getSetting,
        getSettingKey,
        lockPlan,
        unlockPlan,
        requestUnlockOtp,
        verifyUnlockOtp,
        getWeekSummary,
        getMonthSummary
    };
};

export default useCalendarControl;
